import { type Capture, CaptureSchema } from "./schema";

const STORAGE_KEY = "captures";
// Cas C23 : une entrée qui ne passe plus CaptureSchema (ex. capture locale
// d'un ancien format) porte parfois l'uri d'un audio local. `writeAll` ne
// réécrit que les entrées valides ; sans ceci, la prochaine écriture (save,
// remove) l'effacerait en silence. On la déplace donc ICI, telle quelle, dans
// une clé à part qui ne participe jamais à `writeAll`.
const QUARANTINE_KEY = "captures.quarantine";

export interface KeyValueStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export interface CaptureStorage {
  list(): Promise<Capture[]>;
  save(capture: Capture): Promise<void>;
  remove(id: string): Promise<void>;
}

export function createCaptureStorage(kv: KeyValueStorage): CaptureStorage {
  // Lit une clé JSON en tableau brut ; absente, corrompue ou non-tableau ⇒ [].
  async function readJsonArray(key: string): Promise<unknown[]> {
    const raw = await kv.get(key);
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  // Ajoute des entrées brutes (hors schéma) à `captures.quarantine`, sans
  // doublon (comparaison par égalité structurelle). Ne touche jamais à
  // STORAGE_KEY : la quarantaine survit à toute réécriture de `captures`.
  async function quarantine(items: unknown[]): Promise<void> {
    if (items.length === 0) return;
    const existing = await readJsonArray(QUARANTINE_KEY);
    const seen = new Set(existing.map((e) => JSON.stringify(e)));
    const additions = items.filter((item) => !seen.has(JSON.stringify(item)));
    if (additions.length === 0) return;
    await kv.set(QUARANTINE_KEY, JSON.stringify([...existing, ...additions]));
  }

  async function readAll(): Promise<Capture[]> {
    const parsed = await readJsonArray(STORAGE_KEY);
    const valid: Capture[] = [];
    const invalid: unknown[] = [];
    for (const item of parsed) {
      const r = CaptureSchema.safeParse(item);
      if (r.success) valid.push(r.data);
      else invalid.push(item);
    }
    await quarantine(invalid);
    return valid;
  }

  async function writeAll(captures: Capture[]): Promise<void> {
    await kv.set(STORAGE_KEY, JSON.stringify(captures));
  }

  return {
    list: readAll,
    async save(capture) {
      // Validation au point de PERSISTANCE : une capture invalide (ex. durée
      // négative issue d'un currentTime natif aberrant) doit échouer ICI —
      // bruyamment et localement — plutôt que d'être sauvée puis rejetée à la
      // synchro (contrainte DB) ou droppée en silence à la relecture (readAll).
      const parsed = CaptureSchema.safeParse(capture);
      if (!parsed.success) {
        const details = parsed.error.issues
          .map((i) => `${i.path.join(".") || "?"}: ${i.message}`)
          .join(" ; ");
        throw new Error(`Capture invalide, non sauvegardée — ${details}`);
      }
      const current = await readAll();
      const idx = current.findIndex((c) => c.id === parsed.data.id);
      if (idx >= 0) current[idx] = parsed.data;
      else current.push(parsed.data);
      await writeAll(current);
    },
    async remove(id) {
      const current = await readAll();
      const filtered = current.filter((c) => c.id !== id);
      if (filtered.length !== current.length) await writeAll(filtered);
    },
  };
}
